import React from "react";
import { useWallet } from "@/context/WalletContext";
import { usePolling } from "@/components/croc/hooks";
import { Panel, SectionTitle, StatCard, Pill } from "@/components/croc/ui";
import { fmtUsd, pct } from "@/lib/format";
import api from "@/lib/api";
import { Network, Users, Layers, Lock, Check } from "lucide-react";

export default function Levels() {
  const { address, user } = useWallet();
  const { data: config } = usePolling(() => api.config(), [], 120000);
  const { data: ref } = usePolling(() => api.referrals(address), [address], 30000);
  const levels = config?.levels ?? [];
  const directCount = ref?.direct_count ?? 0;
  const liveLevels = ref?.levels ?? [];
  const byLevel = Object.fromEntries(liveLevels.map((l) => [l.level, l]));

  return (
    <div>
      <SectionTitle
        eyebrow="21-Level Override Matrix"
        title="Override Income"
        subtitle="Reward-on-reward paid up to 21 generations. Deeper levels unlock as you add direct referrals."
        testid="levels-title"
      />

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <StatCard label="Direct Referrals" value={directCount} icon={Users} accent="emerald" testid="levels-directs" />
        <StatCard label="Override Income Earned" value={fmtUsd(user?.level_income_earned)} icon={Network} accent="gold" testid="levels-earned" />
        <StatCard label="Active Levels" value={liveLevels.length} icon={Layers} accent="emerald" testid="levels-active" />
      </div>

      <Panel data-testid="levels-matrix">
        <div className="space-y-2">
          {levels.map((l) => {
            const unlocked = directCount >= l.req_directs;
            const live = byLevel[l.level];
            return (
              <div
                key={l.level}
                data-testid={`level-row-${l.level}`}
                className={`flex items-center gap-4 rounded-xl border px-4 py-3 transition-all duration-300 ${
                  unlocked
                    ? "border-[#1CFD4C]/35 bg-gradient-to-r from-[#1CFD4C]/[0.06] to-transparent shadow-[0_0_18px_rgba(212,175,55,0.08)]"
                    : "border-white/10 bg-[#090E0B]"
                }`}
              >
                <div className={`w-9 h-9 rounded-lg grid place-items-center shrink-0 font-num font-bold text-sm ${unlocked ? "bg-[#1CFD4C]/15 gold-text ring-1 ring-[#1CFD4C]/50 shadow-[0_0_12px_rgba(212,175,55,0.35)]" : "bg-white/5 text-muted-foreground"}`}>
                  {l.level}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-white">Level {l.level}</span>
                    {unlocked ? (
                      <Pill tone="emerald"><Check className="w-3 h-3" /> Unlocked</Pill>
                    ) : (
                      <Pill tone="neutral"><Lock className="w-3 h-3" /> {l.req_directs} directs</Pill>
                    )}
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">
                    {live ? `${live.count} members · ${fmtUsd(live.volume)} volume` : "Awaiting downline"}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-num font-bold gold-text">{pct(l.pct * 100)}</div>
                  <div className="text-[10px] text-muted-foreground uppercase tracking-widest">of rewards</div>
                </div>
              </div>
            );
          })}
        </div>
      </Panel>
    </div>
  );
}
